import { useCallback, useEffect, useMemo, useReducer } from "react";
import { createBlock, emptyDoc, isBuilderDoc } from "../schema/factory";

const STORAGE_KEY = "tap_builder_doc";
const HISTORY_LIMIT = 50;

const clone = (v) => JSON.parse(JSON.stringify(v));

const pushHistory = (state, nextDoc) => ({
  ...state,
  past: [...state.past, state.doc].slice(-HISTORY_LIMIT),
  doc: nextDoc,
  future: [],
});

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return { doc: action.doc, selectedId: null, past: [], future: [] };

    case "SELECT":
      return { ...state, selectedId: action.id };

    case "ADD_BLOCK": {
      const block = createBlock(action.blockType);
      const blocks = [...state.doc.blocks];
      const index =
        action.index === undefined ? blocks.length : action.index;
      blocks.splice(index, 0, block);
      return {
        ...pushHistory(state, { ...state.doc, blocks }),
        selectedId: block.id,
      };
    }

    case "UPDATE_BLOCK": {
      const blocks = state.doc.blocks.map((b) =>
        b.id === action.id ? { ...b, props: { ...b.props, ...action.props } } : b
      );
      return pushHistory(state, { ...state.doc, blocks });
    }

    case "REMOVE_BLOCK": {
      const blocks = state.doc.blocks.filter((b) => b.id !== action.id);
      return {
        ...pushHistory(state, { ...state.doc, blocks }),
        selectedId: null,
      };
    }

    case "DUPLICATE_BLOCK": {
      const idx = state.doc.blocks.findIndex((b) => b.id === action.id);
      if (idx === -1) return state;
      const copy = {
        ...clone(state.doc.blocks[idx]),
        id: createBlock(state.doc.blocks[idx].type).id,
      };
      const blocks = [...state.doc.blocks];
      blocks.splice(idx + 1, 0, copy);
      return {
        ...pushHistory(state, { ...state.doc, blocks }),
        selectedId: copy.id,
      };
    }

    case "MOVE_BLOCK": {
      const idx = state.doc.blocks.findIndex((b) => b.id === action.id);
      const target = idx + action.dir;
      if (idx === -1 || target < 0 || target >= state.doc.blocks.length)
        return state;
      const blocks = [...state.doc.blocks];
      const [moved] = blocks.splice(idx, 1);
      blocks.splice(target, 0, moved);
      return pushHistory(state, { ...state.doc, blocks });
    }

    case "REORDER_BLOCK": {
      const from = state.doc.blocks.findIndex((b) => b.id === action.activeId);
      let to = state.doc.blocks.findIndex((b) => b.id === action.overId);
      if (from === -1 || to === -1 || from === to) return state;
      const blocks = [...state.doc.blocks];
      const [moved] = blocks.splice(from, 1);
      blocks.splice(to, 0, moved);
      return pushHistory(state, { ...state.doc, blocks });
    }

    case "ADD_BLOCK_AT": {
      const block = createBlock(action.blockType);
      const blocks = [...state.doc.blocks];
      const overIdx =
        action.overId == null
          ? blocks.length
          : blocks.findIndex((b) => b.id === action.overId);
      const index = overIdx === -1 ? blocks.length : overIdx;
      blocks.splice(index, 0, block);
      return {
        ...pushHistory(state, { ...state.doc, blocks }),
        selectedId: block.id,
      };
    }

    case "UPDATE_SETTINGS":
      return pushHistory(state, {
        ...state.doc,
        settings: { ...state.doc.settings, ...action.settings },
      });

    case "UNDO": {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return {
        ...state,
        doc: previous,
        past: state.past.slice(0, -1),
        future: [state.doc, ...state.future].slice(0, HISTORY_LIMIT),
      };
    }

    case "REDO": {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      return {
        ...state,
        doc: next,
        past: [...state.past, state.doc].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
      };
    }

    default:
      return state;
  }
};

const init = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (isBuilderDoc(stored)) {
      return { doc: stored, selectedId: null, past: [], future: [] };
    }
  } catch (e) {
    // ignore malformed storage
  }
  return { doc: emptyDoc(), selectedId: null, past: [], future: [] };
};

export const useBuilder = () => {
  const [state, dispatch] = useReducer(reducer, undefined, init);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state.doc));
      } catch (e) {
        // storage may be full / unavailable
      }
    }, 400);
    return () => clearTimeout(t);
  }, [state.doc]);

  const selectedBlock = useMemo(
    () => state.doc.blocks.find((b) => b.id === state.selectedId) || null,
    [state.doc.blocks, state.selectedId]
  );

  const actions = useMemo(
    () => ({
      load: (doc) => dispatch({ type: "LOAD", doc }),
      select: (id) => dispatch({ type: "SELECT", id }),
      addBlock: (blockType, index) =>
        dispatch({ type: "ADD_BLOCK", blockType, index }),
      addBlockAt: (blockType, overId) =>
        dispatch({ type: "ADD_BLOCK_AT", blockType, overId }),
      reorderBlock: (activeId, overId) =>
        dispatch({ type: "REORDER_BLOCK", activeId, overId }),
      updateBlock: (id, props) =>
        dispatch({ type: "UPDATE_BLOCK", id, props }),
      removeBlock: (id) => dispatch({ type: "REMOVE_BLOCK", id }),
      duplicateBlock: (id) => dispatch({ type: "DUPLICATE_BLOCK", id }),
      moveBlock: (id, dir) => dispatch({ type: "MOVE_BLOCK", id, dir }),
      updateSettings: (settings) =>
        dispatch({ type: "UPDATE_SETTINGS", settings }),
      undo: () => dispatch({ type: "UNDO" }),
      redo: () => dispatch({ type: "REDO" }),
      reset: () => dispatch({ type: "LOAD", doc: emptyDoc() }),
    }),
    []
  );

  const clearStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    doc: state.doc,
    selectedId: state.selectedId,
    selectedBlock,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    clearStorage,
    ...actions,
  };
};
